import { Button } from 'antd';
import styled, { css, createGlobalStyle } from 'styled-components';
import { jdContentTypography } from '../../theme/jdContentTypography';


// ── SmartDiffViewer global CSS (ported from SmartDiffViewer.scss) ──────────────
export const SmartDiffContainer = styled.div`
  display: flex;
  gap: 0;
  border: 1px solid #dee2e6;
  height: calc(100vh - 250px);
  background: ${p => p.theme.colors.white};
  font-family: 'Century Gothic', sans-serif;
`;

export const DiffPane = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  ${({ $side }) =>
    $side === "left" &&
    css`border-right: 1px solid ${p => p.theme.colors.border};`}
`;

export const PaneHeader = styled.div`
  padding: 12px 16px;
  background: ${p => p.theme.colors.light};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  margin: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${p => p.theme.colors.gray};
`;

export const PaneContent = styled.div`
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding: 16px;
  scroll-behavior: smooth;
  ${jdContentTypography}
`;

// ── Layout styled components ───────────────────────────────────────────────────

export const ContainerStyled = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${p => p.theme.colors.light};

    ${({ $centered }) => $centered && css`
        justify-content: center;
        align-items: center;
    `}
`;

export const HeaderStyled = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: ${p => p.theme.colors.white};
    border-bottom: 1px solid ${p => p.theme.colors.border};
    padding: 4px 8px;
    gap: 16px;
    flex-shrink: 0;
`;

export const ToggleGroupStyled = styled.div`
    display: flex;
    border: 1px solid ${p => p.theme.colors.border};
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
`;

export const ToggleButtonStyled = styled(Button)`
  display: inline-flex;
  align-items: center;
  padding: 8px 20px;
  border: none !important;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary : 'transparent'} !important;

  &:hover, &:focus, &:active {
    border: none !important;
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primary : theme.colors.light} !important;
  }

  & > span {
    color: ${({ $active, theme }) =>
      $active ? theme.colors.white : theme.colors.gray} !important;
  }
`;

export const ViewerAreaStyled = styled.div`
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

// ── History controls (version selectors row) ──────────────────────────────────

export const HistoryControlsStyled = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 1 auto;
    min-width: 0;
`;

export const VsLabelStyled = styled.span`
    font-weight: 700;
    color: ${p => p.theme.colors.gray};
    font-size: 13px;
    white-space: nowrap;
    margin: 0 16px;
    flex: 0 0 auto;
`;

export const SelectWrapperStyled = styled.div`
    flex: 0 1 auto;
    min-width: 0;
    max-width: 280px;

    /* antd's <Select> is inline-block and sizes to its content, which is what we
       want here -- the control hugs the selected tag. max-width (rather than
       width:100%) keeps that intrinsic sizing while capping long tags, so they
       ellipsis instead of pushing into the VS label. */
    .ant-select {
        max-width: 100%;
    }
`;

// ── Approver actions (right side of header) ───────────────────────────────────

export const ApproverActionsStyled = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
`;

export const PendingCountStyled = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${({ $hasRemaining, theme }) => ($hasRemaining ? theme.colors.danger : theme.colors.success)};
    white-space: nowrap;
`;

// ── Viewer states ─────────────────────────────────────────────────────────────

export const EditorWrapperStyled = styled.div`
  height: calc(100vh - 440px);
  overflow-y: auto;
  text-align: start;
  padding: 30px;
  ${jdContentTypography}
`;

export const EditingPanelStyled = styled.div`
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${p => p.theme?.colors?.border ?? '#D8DAE5'};
    display: flex;
    flex-direction: column;
    flex: 1;
`;

export const EmptyStateStyled = styled.div`
    text-align: center;
    padding: 60px 40px;
    color: ${p => p.theme.colors.gray};
`;

export const LoadingOverlayStyled = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    color: ${p => p.theme.colors.gray};
`;

export const DiffRefetchWrapperStyled = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  ${({ $active }) => $active && css`
    & > *:first-child {
      filter: brightness(0.7);
      pointer-events: none;
      user-select: none;
    }
  `}
`;

export const DiffRefetchOverlayStyled = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: ${p => p.theme.colors.gray};
  font-weight: 600;
  z-index: 10;
`;

// ── SmartDiffViewerV3 internal styled components ──────────────────────────────

export const VersionBadgeStyled = styled.span`
    padding: 2px 6px;
    border-radius: 4px;
    background: ${p => p.theme.colors.tertiary};
    color: ${p => p.theme.colors.white};
    font-size: 11px;
    font-weight: 600;
`;

export const DiffLineInnerStyled = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
`;

export const SelectionMenuStyled = styled.div`
    position: fixed;
    top: ${({ $top }) => $top}px;
    left: ${({ $left }) => $left}px;
    background: ${p => p.theme.colors.white};
    border: 1px solid ${p => p.theme.colors.borderLight};
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
    padding: 8px;
    min-width: 220px;
    z-index: 2000;
    animation: popIn 0.2s ease-out;
`;

export const SelectionMenuHeaderStyled = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`;

export const SelectionMenuTitleStyled = styled.strong`
    font-size: 11px;
    color: ${p => p.theme.colors.gray};
`;

export const SelectionMenuActionsStyled = styled.div`
    display: grid;
    gap: 6px;
`;

export const PopoverContentStyled = styled.div`
    display: grid;
    gap: 8px;
`;

export const PopoverDividerStyled = styled.div`
    text-align: center;
`;
